// Oracle DBMS_XPLAN 출력 파서 및 SQL↔실행계획 정밀 매핑
//
// 매핑 전략:
// 1. SQL을 괄호 깊이 기반 Query Block으로 분리
// 2. 각 block 내에서 FROM/JOIN 뒤의 "테이블명 별칭" 위치를 추출
// 3. XPLAN의 Query Block Name(SEL$1, SEL$2)과 Object Alias(E@SEL$1)로 정확한 block↔SQL 매칭
// 4. 동일 테이블명/별칭이 서브쿼리에 중복되어도 정확한 위치 추적 가능

/**
 * XPLAN 텍스트를 구조화된 노드 배열로 파싱
 */
import i18n from '../i18n';
export function parseXplanText(planText) {
  if (!planText) return { nodes: [], predicates: {}, aliases: {}, queryBlocks: {} };

  const lines = planText.split('\n');
  const nodes = [];
  const predicates = {};
  const aliases = {};      // planId → aliasName (예: "E")
  const queryBlocks = {};  // planId → { blockName, alias, fullRef } (예: { blockName:"SEL$1", alias:"E", fullRef:"E@SEL$1" })

  let section = 'plan';
  let predId = null;   // 조건 절에서 지금 이어 붙이는 계획 줄 번호

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (/^Predicate\s+Information/i.test(line.trim())) { section = 'predicate'; continue; }
    if (/^Query\s+Block\s+Name/i.test(line.trim()) || /^Object\s+Alias/i.test(line.trim())) { section = 'alias'; continue; }
    if (/^Note/i.test(line.trim()) || /^Column\s+Projection/i.test(line.trim()) || /^Hint\s+Report/i.test(line.trim())) { section = 'other'; continue; }
    if (line.trim().startsWith('---')) continue;

    // Plan 행 파싱
    if (section === 'plan') {
      // 연산 칸의 앞 공백이 계층 깊이다 — 칸 구분 뒤 첫 공백 하나는 빼고 센다(예전 식은 공백을 다 먹어 깊이가 늘 0 이었다, 2026-10-26)
      const m = line.match(/^\|\s*(\*?)\s*(\d+)\s*\|( +)(.+?)\s*\|\s*(.*?)\s*\|/);
      if (m) {
        nodes.push({
          id: parseInt(m[2]),
          operation: m[4].trim(),
          objectName: m[5].trim() || null,
          depth: Math.max(0, m[3].length - 1),
          hasAsterisk: m[1] === '*',
          lineIndex: i,
          rawLine: line,
        });
      }
    }

    // Predicate 파싱 — 번호로 시작하는 줄만 읽으면 안 된다(2026-10-27 신고 「첫 줄만 보이고 잘린다」).
    // DBMS_XPLAN 은 ① 긴 조건을 다음 줄로 넘겨 쓰고(들여쓴 이어짐 줄) ② 같은 번호의 두 번째 조건(access 뒤 filter)도 번호 없이 다음 줄에 쓴다.
    //    2 - access("A"."ID"=:B1)
    //        filter(("A"."ST"='Y' AND
    //               "A"."DT">SYSDATE-1))
    // 둘 다 바로 앞 번호에 붙이고, 끝 괄호는 다 모은 뒤에 한 번만 뗀다. 빈 줄이 오면 그 번호는 끝이다.
    if (section === 'predicate') {
      const pm = line.match(/^\s*(\d+)\s*-\s*(access|filter)\((.*)$/);
      const cm = !pm && line.match(/^\s*(access|filter)\((.*)$/);
      if (pm) {
        predId = parseInt(pm[1]);
        if (!predicates[predId]) predicates[predId] = [];
        predicates[predId].push({ type: pm[2], condition: pm[3].trim() });
      } else if (cm && predId != null) {
        predicates[predId].push({ type: cm[1], condition: cm[2].trim() });
      } else if (line.trim() && predId != null) {
        const list = predicates[predId];
        const last = list[list.length - 1];
        last.condition = `${last.condition} ${line.trim()}`;
      } else if (!line.trim()) {
        predId = null;
      }
    }

    // Alias + Query Block 파싱: "   2 - SEL$1 / E@SEL$1"
    if (section === 'alias') {
      const am = line.match(/^\s*(\d+)\s*-\s*(\S+)\s*(?:\/\s*(\w+)@(\S+))?\s*$/);
      if (am) {
        const planId = parseInt(am[1]);
        const blockName = am[2];      // SEL$1
        const aliasName = am[3] || null; // E
        const aliasBlock = am[4] || null; // SEL$1

        if (aliasName) {
          aliases[planId] = aliasName;
        }
        queryBlocks[planId] = {
          blockName,
          alias: aliasName,
          aliasBlock,
          fullRef: am[3] ? `${am[3]}@${am[4]}` : null,
        };
      }
    }
  }

  // 「access(」 의 짝 괄호 — 이어진 줄까지 모은 뒤 끝의 하나만 뗀다
  Object.values(predicates).forEach(list => list.forEach(p => {
    if (p.condition.endsWith(')')) p.condition = p.condition.slice(0, -1).trim();
  }));

  return { nodes, predicates, aliases, queryBlocks };
}

// ============================================================
// SQL Query Block 파서 — 괄호 깊이 기반으로 서브쿼리 영역 분리
// ============================================================

/**
 * SQL을 Query Block으로 분리
 * 메인 쿼리 = block 0, 첫 번째 서브쿼리 = block 1, ...
 * 각 block은 SQL 원본 내의 from/to offset과 내부 텍스트를 가짐
 * @returns {Array<{ blockIndex, from, to, text, depth }>}
 */
function splitSqlQueryBlocks(sql) {
  const blocks = [];
  let depth = 0;
  let blockStart = 0;
  let blockIndex = 0;
  const parenStack = []; // 각 깊이의 시작 위치

  // depth 0 = 메인 쿼리
  blocks.push({ blockIndex: 0, from: 0, to: sql.length, text: sql, depth: 0 });

  for (let i = 0; i < sql.length; i++) {
    const ch = sql[i];
    if (ch === "'") {
      // 문자열 리터럴 스킵
      i++;
      while (i < sql.length) {
        if (sql[i] === "'" && sql[i + 1] === "'") { i += 2; continue; }
        if (sql[i] === "'") break;
        i++;
      }
      continue;
    }
    if (ch === '(') {
      depth++;
      parenStack.push(i);
      // 괄호 안에 SELECT가 있으면 서브쿼리 블록
      const ahead = sql.substring(i + 1, i + 30).trim().toUpperCase();
      if (ahead.startsWith('SELECT')) {
        blockIndex++;
        blocks.push({
          blockIndex,
          from: i + 1,
          to: -1, // 나중에 닫는 괄호에서 설정
          text: '',
          depth,
        });
      }
    }
    if (ch === ')') {
      // 열린 서브쿼리 블록 중 아직 닫히지 않은 것 찾기
      for (let b = blocks.length - 1; b >= 0; b--) {
        if (blocks[b].depth === depth && blocks[b].to === -1) {
          blocks[b].to = i;
          blocks[b].text = sql.substring(blocks[b].from, i);
          break;
        }
      }
      depth--;
      parenStack.pop();
    }
  }

  return blocks;
}

/**
 * 하나의 SQL 블록 내에서 FROM/JOIN 뒤의 [schema.]table [alias] 위치를 추출
 * @param {string} blockText - SQL 블록 텍스트
 * @param {number} globalOffset - 원본 SQL 내 이 블록의 시작 offset
 * @returns {Array<{ tableName, aliasName, tableFrom, tableTo, aliasFrom, aliasTo }>}
 */
function findTableRefsInBlock(blockText, globalOffset) {
  const results = [];
  const SQL_KEYWORDS = new Set([
    'ON','WHERE','AND','OR','SET','INTO','VALUES','AS','LEFT','RIGHT','INNER','OUTER',
    'CROSS','FULL','NATURAL','GROUP','ORDER','HAVING','SELECT','FROM','JOIN','UNION',
    'MINUS','INTERSECT','EXCEPT','WITH','USING','BETWEEN','LIKE','IN','NOT','EXISTS',
    'CASE','WHEN','THEN','ELSE','END','DISTINCT','ALL','ANY','SOME','FETCH','OFFSET',
    'LIMIT','FOR','UPDATE','DELETE','INSERT','MERGE','CREATE','ALTER','DROP','GRANT',
    'PARTITION','SUBPARTITION','PIVOT','UNPIVOT','MODEL','CONNECT','START','PRIOR',
    'NOCYCLE','LATERAL','XMLTABLE','TABLE','ROWNUM','LEVEL','NULLS','FIRST','LAST',
    'ASC','DESC','BY','OVER','ROWS','RANGE','UNBOUNDED','PRECEDING','FOLLOWING','CURRENT',
  ]);

  // FROM/JOIN 뒤의 패턴 매칭
  const pattern = /\b(?:FROM|JOIN)\s+(?:(\w+)\.)?(\w+)(?:\s+(\w+))?\b/gi;
  let m;
  while ((m = pattern.exec(blockText)) !== null) {
    const tableName = m[2].toUpperCase();
    const possibleAlias = m[3] ? m[3].toUpperCase() : null;

    // 테이블명 자체가 키워드인 경우 스킵 (예: FROM DUAL)
    // alias가 키워드이면 alias 없는 것으로 처리
    const aliasName = possibleAlias && !SQL_KEYWORDS.has(possibleAlias) ? possibleAlias : null;

    const schemaPrefix = m[1] ? m[1].length + 1 : 0;
    const tableStartInBlock = m.index + m[0].indexOf(m[2]);
    const tableFrom = globalOffset + tableStartInBlock;
    const tableTo = tableFrom + m[2].length;

    let aliasFrom = -1, aliasTo = -1;
    if (aliasName && m[3]) {
      const aliasStartInBlock = m.index + m[0].lastIndexOf(m[3]);
      aliasFrom = globalOffset + aliasStartInBlock;
      aliasTo = aliasFrom + m[3].length;
    }

    results.push({ tableName, aliasName, tableFrom, tableTo, aliasFrom, aliasTo });
  }

  // 콤마로 나열된 테이블도 처리: FROM a, b c → b는 별도 패턴 필요
  // 이미 FROM ... 패턴에서 첫 번째는 잡히므로, 콤마 뒤의 것을 추가 처리
  const commaPattern = /,\s+(?:(\w+)\.)?(\w+)(?:\s+(\w+))?\b/gi;
  while ((m = commaPattern.exec(blockText)) !== null) {
    const tableName = m[2].toUpperCase();
    const possibleAlias = m[3] ? m[3].toUpperCase() : null;
    const aliasName = possibleAlias && !SQL_KEYWORDS.has(possibleAlias) ? possibleAlias : null;

    const tableStartInBlock = m.index + m[0].indexOf(m[2]);
    const tableFrom = globalOffset + tableStartInBlock;
    const tableTo = tableFrom + m[2].length;

    let aliasFrom = -1, aliasTo = -1;
    if (aliasName && m[3]) {
      const aliasStartInBlock = m.index + m[0].lastIndexOf(m[3]);
      aliasFrom = globalOffset + aliasStartInBlock;
      aliasTo = aliasFrom + m[3].length;
    }

    // 중복 방지
    if (!results.some(r => r.tableFrom === tableFrom)) {
      results.push({ tableName, aliasName, tableFrom, tableTo, aliasFrom, aliasTo });
    }
  }

  return results;
}

// ============================================================
// XPLAN Query Block ↔ SQL Query Block 매칭
// ============================================================

/**
 * Oracle의 SEL$1, SEL$2 등의 query block 순서를 SQL의 서브쿼리 순서에 매핑
 * SEL$1 = 메인 쿼리 (depth 0), SEL$2 = 첫 번째 서브쿼리, ...
 * Oracle의 실제 번호는 변환 규칙이 복잡하므로, 등장 순서 기반으로 매핑
 */
function mapQueryBlocksToSqlBlocks(queryBlocks, sqlBlocks) {
  // queryBlocks에서 고유한 blockName 추출 (등장 순서)
  const seen = new Set();
  const uniqueBlocks = [];
  for (const [, qb] of Object.entries(queryBlocks).sort((a, b) => parseInt(a[0]) - parseInt(b[0]))) {
    const bn = qb.aliasBlock || qb.blockName;
    if (bn && !seen.has(bn)) {
      seen.add(bn);
      uniqueBlocks.push(bn);
    }
  }

  // SQL blocks는 blockIndex 순서 (0=메인, 1=첫번째 서브쿼리, ...)
  const blockMap = new Map(); // blockName → sqlBlock
  for (let i = 0; i < uniqueBlocks.length && i < sqlBlocks.length; i++) {
    blockMap.set(uniqueBlocks[i], sqlBlocks[i]);
  }

  return blockMap;
}

// ============================================================
// 메인 매핑 함수
// ============================================================

/**
 * 실행계획 노드와 SQL 텍스트 간의 정밀 매핑
 * Query Block 기반으로 서브쿼리 내 동일 별칭도 정확히 구분
 */
export function buildPlanToSqlMapping(parsedPlan, sql) {
  const { nodes, predicates, aliases, queryBlocks } = parsedPlan;
  const mapping = new Map();
  const upperSql = sql.toUpperCase();

  // 1. SQL을 Query Block으로 분리
  const sqlBlocks = splitSqlQueryBlocks(sql);

  // 2. 각 SQL block 내 테이블 참조 추출
  const blockTableRefs = new Map(); // blockIndex → [tableRef]
  for (const block of sqlBlocks) {
    blockTableRefs.set(block.blockIndex, findTableRefsInBlock(block.text, block.from));
  }

  // 3. XPLAN query block ↔ SQL block 매핑
  const qbToSqlBlock = mapQueryBlocksToSqlBlocks(queryBlocks || {}, sqlBlocks);

  // 4. 모든 테이블 참조를 평면 배열로도 유지 (폴백용)
  const allTableRefs = [];
  for (const refs of blockTableRefs.values()) {
    allTableRefs.push(...refs);
  }

  for (const node of nodes) {
    const highlights = [];
    let alias = aliases[node.id];
    let objUpper = node.objectName ? node.objectName.toUpperCase() : null;
    // **인덱스 줄은 그 테이블로**(2026-10-26 신고 — 인덱스 이름은 쿼리에 없어 아무것도 가리키지 못했다).
    // 바로 위 부모가 `TABLE ACCESS BY … ROWID 테이블` 이면 그 테이블·별칭을, 인덱스만 읽는 줄이면 조건(access)의 별칭으로 찾는다
    if (/\bINDEX\b/.test(node.operation) && !/^TABLE ACCESS/.test(node.operation)) {
      const parent = parentOf(nodes, node);
      if (parent && /^TABLE ACCESS\b.*\bROWID\b/.test(parent.operation) && parent.objectName) {
        objUpper = parent.objectName.toUpperCase();
        if (!alias && aliases[parent.id]) alias = aliases[parent.id];
      } else {
        objUpper = null;   // 인덱스 이름으로는 찾지 않는다
        // 테이블을 모를 때만 조건의 별칭으로 — 조인 조건("O"."ID"="C"."ID")의 별칭은 어느 쪽인지 모호하다
        if (!alias) alias = aliasFromPredicates(predicates[node.id]);
      }
    }
    const qb = queryBlocks ? queryBlocks[node.id] : null;
    let matched = false;

    // === 정밀 매칭: Query Block + Alias ===
    if (alias && objUpper && qb) {
      const aliasUpper = alias.toUpperCase();
      const blockName = qb.aliasBlock || qb.blockName;
      const sqlBlock = qbToSqlBlock.get(blockName);

      if (sqlBlock) {
        // 해당 SQL block의 테이블 참조에서 테이블명+별칭 매칭
        const refs = blockTableRefs.get(sqlBlock.blockIndex) || [];
        const pos = refs.find(r => r.tableName === objUpper && r.aliasName === aliasUpper);
        if (pos) {
          highlights.push({ from: pos.tableFrom, to: pos.tableTo, type: 'table', label: `${node.objectName} (${alias}) [${blockName}]` });
          if (pos.aliasFrom >= 0) {
            highlights.push({ from: pos.aliasFrom, to: pos.aliasTo, type: 'alias', label: i18n.t('common:xp.alias', { alias: alias, object: node.objectName }) });
          }
          matched = true;
        }
      }
    }

    // === 차선 매칭: 전체 SQL에서 테이블명+별칭 ===
    if (!matched && alias && objUpper) {
      const aliasUpper = alias.toUpperCase();
      const pos = allTableRefs.find(r => r.tableName === objUpper && r.aliasName === aliasUpper);
      if (pos) {
        highlights.push({ from: pos.tableFrom, to: pos.tableTo, type: 'table', label: `${node.objectName} (${alias})` });
        if (pos.aliasFrom >= 0) {
          highlights.push({ from: pos.aliasFrom, to: pos.aliasTo, type: 'alias', label: i18n.t('common:xp.alias', { alias: alias, object: node.objectName }) });
        }
        matched = true;
      }
    }

    // === 폴백: alias만으로 매칭 ===
    if (!matched && alias) {
      const aliasUpper = alias.toUpperCase();
      const pos = allTableRefs.find(r => r.aliasName === aliasUpper);
      if (pos) {
        highlights.push({ from: pos.tableFrom, to: pos.tableTo, type: 'table', label: node.objectName || alias });
        matched = true;
      }
    }

    // === 폴백: 테이블명 첫 매칭 ===
    if (!matched && objUpper) {
      const regex = new RegExp(`\\b${escapeRegex(objUpper)}\\b`);
      const m = regex.exec(upperSql);
      if (m) {
        highlights.push({ from: m.index, to: m.index + objUpper.length, type: 'table', label: node.objectName });
      }
    }

    // === Predicate — 조건절의 **조건식 전체**를 ===
    // 예전에는 컬럼을 쿼리 전체에서 처음 나오는 곳에서 찾아 SELECT 목록을 칠했다(2026-10-26 신고). 이제 WHERE·ON·HAVING 구간 안에서 찾고,
    // 그 컬럼이 든 조건식(AND·OR 사이)을 칠한다. 조건 구간에서 못 찾으면 칠하지 않는다(엉뚱한 곳보다 낫다)
    const preds = predicates[node.id];
    if (preds) {
      const zones = conditionZones(sql);
      for (const pred of preds) {
        const colRefs = pred.condition.match(/"(\w+)"\."(\w+)"/g) || [];
        const bare = colRefs.length ? [] : (pred.condition.match(/"(\w+)"/g) || []);
        const targets = [
          ...colRefs.map(r => { const [al, col] = r.replace(/"/g, '').split('.'); return { al: al.toUpperCase(), col: col.toUpperCase() }; }),
          ...bare.map(r => ({ al: null, col: r.replace(/"/g, '').toUpperCase() })),
        ];
        for (const { al, col } of targets) {
          const patterns = al ? [new RegExp(`\\b${escapeRegex(al)}\\s*\\.\\s*${escapeRegex(col)}\\b`, 'gi'), new RegExp(`\\b${escapeRegex(col)}\\b`, 'gi')]
            : [new RegExp(`\\b${escapeRegex(col)}\\b`, 'gi')];
          let hit = null;
          for (const re of patterns) {
            let m;
            while (!hit && (m = re.exec(sql)) !== null) {
              const z = zones.find(zz => m.index >= zz.from && m.index < zz.to);
              if (z) hit = conditionTerm(sql, z, m.index, m.index + m[0].length);
            }
            if (hit) break;
          }
          if (hit) highlights.push({ from: hit.from, to: hit.to, type: pred.type, label: `${pred.type}: ${pred.condition}` });
        }
      }
    }

    // 중복 제거
    const unique = [];
    for (const h of highlights) {
      if (!unique.some(u => u.from === h.from && u.to === h.to)) unique.push(h);
    }
    if (unique.length > 0) mapping.set(node.id, unique);
  }

  return mapping;
}

/**
 * SQL 위치 → Plan ID 역매핑
 */
export function buildSqlToPlanMapping(planToSql) {
  const reverse = new Map();
  for (const [planId, highlights] of planToSql) {
    for (const h of highlights) {
      const key = `${h.from}:${h.to}`;
      if (!reverse.has(key)) reverse.set(key, []);
      reverse.get(key).push(planId);
    }
  }
  return reverse;
}

/** 바로 위 부모 — 앞쪽에서 깊이가 더 얕은 첫 줄 */
function parentOf(nodes, node) {
  const i = nodes.indexOf(node);
  for (let k = i - 1; k >= 0; k -= 1) if (nodes[k].depth < node.depth) return nodes[k];
  return null;
}

/** 조건의 `"별칭"."컬럼"` 에서 별칭 하나(인덱스만 읽는 줄이 어느 테이블인지) */
function aliasFromPredicates(preds) {
  for (const p of preds || []) {
    const m = /"(\w+)"\."\w+"/.exec(p.condition);
    if (m) return m[1];
  }
  return null;
}

const ZONE_START = /\b(WHERE|ON|HAVING|START\s+WITH|CONNECT\s+BY)\b/gi;
const ZONE_END = /\b(GROUP\s+BY|ORDER\s+BY|HAVING|UNION|MINUS|INTERSECT|EXCEPT|FETCH|SELECT|FROM|WHERE|JOIN|INNER|LEFT|RIGHT|FULL|CROSS|CONNECT\s+BY|START\s+WITH|MODEL|WINDOW)\b/gi;

/**
 * 조건 구간 — WHERE·ON·HAVING·START WITH·CONNECT BY 다음부터 다음 절(GROUP BY·ORDER BY·SELECT·FROM·JOIN …)까지.
 * 주석·문자열 안의 낱말은 보지 않는다(같은 길이의 빈칸으로 가린 글에서 찾는다).
 */
export function conditionZones(sql) {
  const masked = String(sql || '')
    .replace(/--[^\n]*/g, (x) => ' '.repeat(x.length))
    .replace(/\/\*[\s\S]*?\*\//g, (x) => x.replace(/[^\n]/g, ' '))
    .replace(/'(?:[^']|'')*'/g, (x) => `'${' '.repeat(Math.max(0, x.length - 2))}'`);
  const zones = [];
  ZONE_START.lastIndex = 0;
  let m;
  while ((m = ZONE_START.exec(masked)) !== null) {
    const from = m.index + m[0].length;
    ZONE_END.lastIndex = from;
    let e = ZONE_END.exec(masked);
    // 부분 질의(괄호 안 SELECT)는 구간을 끝내지만, 그 괄호가 닫힌 뒤로도 조건이 이어질 수 있다 — 단순하게 다음 절까지만
    const to = e ? e.index : masked.length;
    if (to > from) zones.push({ from, to, masked });
  }
  return zones;
}

/** 조건 구간 안에서 [s, e) 를 품은 조건식 하나 — AND·OR·괄호 경계까지 넓힌다(괄호 깊이 0 기준) */
function conditionTerm(sql, zone, s, e) {
  const t = zone.masked;
  const isBoundary = (i, dir) => {
    const rest = dir < 0 ? t.slice(Math.max(zone.from, i - 4), i) : t.slice(i, i + 4);
    return dir < 0 ? /(^|\s)(AND|OR)\s$/i.test(rest) || /\bAND\s?$/i.test(rest) : /^\s(AND|OR)(\s|$)/i.test(rest);
  };
  let depth = 0;
  let from = s;
  for (let i = s - 1; i >= zone.from; i -= 1) {
    const c = t[i];
    if (c === ')') depth += 1;
    else if (c === '(') { if (depth === 0) break; depth -= 1; }
    if (depth === 0 && /\s/.test(c) && isBoundary(i + 1, -1)) break;
    from = i;
  }
  depth = 0;
  let to = e;
  for (let i = e; i < zone.to; i += 1) {
    const c = t[i];
    if (c === '(') depth += 1;
    else if (c === ')') { if (depth === 0) break; depth -= 1; }
    if (depth === 0 && /\s/.test(c) && isBoundary(i, 1)) break;
    to = i + 1;
  }
  // 앞뒤 공백·쉼표는 뺀다
  while (from < to && /[\s,]/.test(t[from])) from += 1;
  while (to > from && /[\s,;]/.test(t[to - 1])) to -= 1;
  return { from, to };
}

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export default parseXplanText;
